import { ReactElement, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, IconButton, Radio, RadioGroup } from '@breathelife/mui';
import { ESignSigner, ESignSignerAuthMethod } from '@breathelife/types';
import { ImageTypes, PhoneInput, Input as TextInput } from '@breathelife/ui-components';

import { StyledFormControlLabel } from '../../../Checkbox/Styles';
import { Icon } from '../../../Icons';
import { SubmitButton } from '../../../Button/SubmitButton';
import Typography from '../../../Typography';
import { ModalLayout } from '../../../../Layouts/Modal/ModalLayout';
import { SignerInfoHeader } from '../../Drawers/ESignatureDetails/SignerInfo/SignerInfoHeader';
import { getFieldValidationError, SignerFormField } from '../../../../Helpers/inputValidation/form/signer';
import { useCarrierContext } from '../../../../Hooks';
import { useEditSignerMutation } from '../../../../ReactQuery/ESignCeremony/eSignSignerActions.mutations';
import styled from '../../../../Styles/themed-styled-components';

type EditSignerModalProps = {
  signer: ESignSigner;
  applicationId: string;
  isOpen: boolean;
  onClose: () => void;
};

const StyledTextInput = styled(TextInput)`
  span {
    font-size: 14px !important;
  }
`;

const AuthMethodContainer = styled.div`
  display: flex;
  align-items: center;

  * {
    min-width: fit-content;
  }
`;

export function EditSignerModal(props: EditSignerModalProps): ReactElement {
  const { signer, applicationId, isOpen, onClose } = props;
  const { t } = useTranslation();
  const { features } = useCarrierContext();
  const [maskPasscode, setMaskPasscode] = useState(true);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const [email, setEmail] = useState(signer.email ?? '');
  const [authMethod, setAuthMethod] = useState<ESignSignerAuthMethod>(
    signer.authMethod ?? ESignSignerAuthMethod.cellphone,
  );
  const [cellphone, setCellphone] = useState(signer.cellphone ?? '');
  const [passcode, setPasscode] = useState(signer.passcode ?? '');

  const editSignerMutation = useEditSignerMutation({
    onSuccess: () => onClose(),
  });

  const enabledAuthMethods: ESignSignerAuthMethod[] = features.eSignature.enabledTwoFactorAuthenticationTypes ?? [
    ESignSignerAuthMethod.cellphone,
    ESignSignerAuthMethod.passcode,
  ];

  const emailError = hasSubmitted ? getFieldValidationError(SignerFormField.email, email) : undefined;
  const cellphoneError =
    hasSubmitted && authMethod === ESignSignerAuthMethod.cellphone
      ? getFieldValidationError(SignerFormField.cellphone, cellphone)
      : undefined;
  const passcodeError =
    hasSubmitted && authMethod === ESignSignerAuthMethod.passcode
      ? getFieldValidationError(SignerFormField.passcode, passcode)
      : undefined;

  const onAuthMethodChange = useCallback((newMethod: ESignSignerAuthMethod) => {
    setAuthMethod(newMethod);
  }, []);

  const onSave = useCallback(() => {
    setHasSubmitted(true);

    const hasEmailError = !!getFieldValidationError(SignerFormField.email, email);
    const hasCellphoneError =
      authMethod === ESignSignerAuthMethod.cellphone && !!getFieldValidationError(SignerFormField.cellphone, cellphone);
    const hasPasscodeError =
      authMethod === ESignSignerAuthMethod.passcode && !!getFieldValidationError(SignerFormField.passcode, passcode);

    if (hasEmailError || hasCellphoneError || hasPasscodeError) return;

    editSignerMutation.mutate({
      signerId: signer.id,
      applicationId,
      email,
      authMethod,
      ...(authMethod === ESignSignerAuthMethod.cellphone ? { cellphone } : { passcode }),
    });
  }, [signer.id, applicationId, email, authMethod, cellphone, passcode, editSignerMutation]);

  return (
    <ModalLayout
      ariaLabel={t('assistedApplication.signerActions.editSigner')}
      isOpen={isOpen}
      closeModal={onClose}
      maxWidth='sm'
      disableBodyPadding
      submitButton={
        <SubmitButton isLoading={editSignerMutation.isLoading} onClick={onSave}>
          {t('assistedApplication.save')}
        </SubmitButton>
      }
    >
      <SignerInfoHeader signer={signer} />

      <Box px={3} pb={3} pt={1}>
        <Box mb={2} maxWidth={310}>
          <StyledTextInput
            fullWidth
            required
            inputVariant='outlined'
            label={t('inputs.email')}
            error={!!emailError}
            validationError={emailError}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            inputTooltip={{
              icon: <Icon name='info' variant='primary' width='16' height='16' imageType={ImageTypes.decorative} />,
              text: t('assistedApplication.eSignatureDetails.emailInfo'),
            }}
          />
        </Box>

        {enabledAuthMethods.length > 1 && (
          <AuthMethodContainer>
            <Typography variant='body3'>{t('assistedApplication.eSignatureDetails.authMethod')}</Typography>
            <RadioGroup
              row
              value={authMethod}
              onChange={(e) => onAuthMethodChange(e.target.value as ESignSignerAuthMethod)}
              sx={{ marginLeft: '30px' }}
            >
              {enabledAuthMethods.map((method) => (
                <StyledFormControlLabel
                  key={method}
                  control={<Radio size='small' />}
                  label={t(`assistedApplication.eSignatureDetails.${method}`)}
                  value={method}
                  checked={method === authMethod}
                />
              ))}
            </RadioGroup>
          </AuthMethodContainer>
        )}

        <Box mt={1}>
          {authMethod === ESignSignerAuthMethod.cellphone && (
            <Box maxWidth={175}>
              <PhoneInput
                name='editSigner-phoneNumber'
                required
                inputVariant='outlined'
                label={enabledAuthMethods.length === 1 ? t('inputs.cellNumber') : ''}
                validationError={cellphoneError}
                value={cellphone}
                onAnswerChange={(answer: string) => setCellphone(answer)}
              />
            </Box>
          )}
          {authMethod === ESignSignerAuthMethod.passcode && (
            <Box maxWidth={200}>
              <TextInput
                fullWidth
                type={maskPasscode ? 'password' : 'text'}
                required
                inputVariant='outlined'
                label={enabledAuthMethods.length === 1 ? t('assistedApplication.eSignatureDetails.passcode') : ''}
                error={!!passcodeError}
                validationError={passcodeError}
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                inputTooltip={{
                  icon: (
                    <IconButton onClick={() => setMaskPasscode(!maskPasscode)}>
                      <Icon
                        name={maskPasscode ? 'visibility' : 'visibilityOff'}
                        variant='primary'
                        stroke='none'
                        imageType={ImageTypes.decorative}
                        width='25px'
                        height='25px'
                      />
                    </IconButton>
                  ),
                  text: maskPasscode
                    ? t('assistedApplication.eSignatureDetails.show')
                    : t('assistedApplication.eSignatureDetails.hide'),
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </ModalLayout>
  );
}
